import { useState, useEffect, useRef } from 'react';
import {
  Copy, Check, Building2, Calendar, Link2,
  Briefcase, ArrowRight, CheckCircle, Rocket,
  Palette, Upload, ChevronDown, ChevronRight, Settings2,
  CreditCard, Shield, Zap, MessageSquare, Eye, EyeOff,
} from 'lucide-react';
import { useWorkspace, useUpdateWorkspace, useUploadLogo, useForms, useBillingStatus, useCreateCheckout, useBillingPortal } from '../hooks/useApi';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

const APP_URL = 'https://ihirefast.com';
const API_URL = import.meta.env.VITE_API_URL || '';

export default function SettingsPage() {
  const { workspace, user } = useAuthStore();
  const { data, isLoading } = useWorkspace();
  const { data: formsData } = useForms();
  const { data: billingData } = useBillingStatus();
  const createCheckout = useCreateCheckout();
  const billingPortal = useBillingPortal();
  const updateWorkspace = useUpdateWorkspace();
  const uploadLogo = useUploadLogo();
  const navigate = useNavigate();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const [billingSuccess, setBillingSuccess] = useState(false);

  // Check for Stripe redirect success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      setBillingSuccess(true);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setBillingSuccess(false), 5000);
    }
  }, []);

  const handleCheckout = async () => {
    try {
      const result = await createCheckout.mutateAsync();
      if (result.url) {
        window.location.href = result.url;
      } else {
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Checkout failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handlePortal = async () => {
    try {
      const result = await billingPortal.mutateAsync();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Portal error:', error);
    }
  };

  const [formData, setFormData] = useState({
    companyName: '',
    displayName: '',
    calendlyLink: '',
    formBackgroundColor: '#f0f4ff',
    primaryColor: '#2563eb',
    httpsmsApiKey: '',
    httpsmsPhone: '',
  });

  useEffect(() => {
    if (data?.workspace) {
      setFormData({
        companyName: data.workspace.companyName || '',
        displayName: data.workspace.displayName || '',
        calendlyLink: data.workspace.calendlyLink || '',
        formBackgroundColor: data.workspace.formBackgroundColor || '#f0f4ff',
        primaryColor: data.workspace.primaryColor || '#2563eb',
        httpsmsApiKey: '',
        httpsmsPhone: (data.workspace as any).httpsmsPhone || '',
      });
      if (data.workspace.logoUrl) {
        setLogoPreview(data.workspace.logoUrl);
      }
    }
  }, [data]);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2500);
  };

  const handleSave = async () => {
    const payload: Record<string, string | undefined> = {
      companyName: formData.companyName,
      displayName: formData.displayName || undefined,
      calendlyLink: formData.calendlyLink || undefined,
      formBackgroundColor: formData.formBackgroundColor || undefined,
      primaryColor: formData.primaryColor || undefined,
      httpsmsPhone: formData.httpsmsPhone || undefined,
    };
    // Only send API key if user typed a new one (not the masked placeholder)
    if (formData.httpsmsApiKey && formData.httpsmsApiKey !== '••••••••') {
      payload.httpsmsApiKey = formData.httpsmsApiKey;
    }
    await updateWorkspace.mutateAsync(payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Logo must be under 2MB');
      return;
    }

    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);

    try {
      await uploadLogo.mutateAsync(file);
    } catch {
      setLogoPreview(data?.workspace?.logoUrl || null);
    }
  };

  const workspaceSlug = workspace?.slug || '';

  const activeForms = formsData?.forms?.filter((f: any) => f.status === 'active') || [];
  const allForms = formsData?.forms || [];
  const hasActiveForms = activeForms.length > 0;
  const hasAnyForms = allForms.length > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">

      {/* Billing success toast */}
      {billingSuccess && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800 font-medium">
            Subscription activated! You now have full access to all features.
          </p>
        </div>
      )}

      {/* ============================= */}
      {/* PLAN & BILLING                */}
      {/* ============================= */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-primary-600" />
          <h3 className="font-medium text-gray-900">Plan & Billing</h3>
        </div>

        {!billingData ? (
          <div className="space-y-4">
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-gray-900">$25</span>
              <span className="text-gray-500">/month</span>
            </div>
            <p className="text-sm text-gray-500">14-day free trial &middot; Cancel anytime</p>
            <ul className="space-y-2">
              {[
                'Unlimited application forms',
                'Unlimited submissions',
                'Unlimited applicants',
                'Email campaigns',
                'Custom branding',
                'Calendly integration',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            <button
              onClick={handleCheckout}
              disabled={createCheckout.isPending}
              className="btn-primary inline-flex items-center gap-2"
            >
              {createCheckout.isPending ? 'Loading...' : 'Start 14-Day Free Trial'}
            </button>
          </div>
        ) : billingData?.grandfathered ? (
          <div className="space-y-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
              <Shield className="w-4 h-4" />
              Grandfathered — Free Forever
            </span>
            <p className="text-sm text-gray-600">
              You have full access to all features at no cost.
            </p>
          </div>
        ) : billingData?.status === 'active' || billingData?.status === 'trialing' ? (
          <div className="space-y-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
              <Zap className="w-4 h-4" />
              Pro — {billingData.status === 'trialing' ? 'Trial Active' : 'Active'}
            </span>
            <p className="text-sm text-gray-600">
              {billingData.status === 'trialing'
                ? 'Your 14-day free trial is active. You have full access to all features.'
                : 'You have full access to all features.'}
            </p>
            <button
              onClick={handlePortal}
              disabled={billingPortal.isPending}
              className="btn-secondary inline-flex items-center gap-2 text-sm"
            >
              {billingPortal.isPending ? 'Loading...' : 'Manage Subscription'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-gray-900">$25</span>
              <span className="text-gray-500">/month</span>
            </div>
            <p className="text-sm text-gray-500">14-day free trial &middot; Cancel anytime</p>
            <ul className="space-y-2">
              {[
                'Unlimited application forms',
                'Unlimited submissions',
                'Unlimited applicants',
                'Email campaigns',
                'Custom branding',
                'Calendly integration',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            <button
              onClick={handleCheckout}
              disabled={createCheckout.isPending}
              className="btn-primary inline-flex items-center gap-2"
            >
              {createCheckout.isPending ? 'Loading...' : 'Start 14-Day Free Trial'}
            </button>
          </div>
        )}
      </div>

      {/* ============================= */}
      {/* GET STARTED / HIRING SETUP    */}
      {/* ============================= */}
      <div className="card overflow-hidden">
        <div className="bg-gradient-to-r from-primary-600 to-blue-500 px-6 py-5">
          <div className="flex items-center gap-3">
            <Rocket className="w-7 h-7 text-white" />
            <div>
              <h3 className="text-xl font-bold text-white">Start Receiving Applications</h3>
              <p className="text-blue-100 text-sm mt-0.5">3 easy steps to connect Indeed, LinkedIn, or any job board</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">

          {/* STEP 1 */}
          <div className="flex gap-4">
            <div className={cn(
              "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm",
              hasActiveForms
                ? "bg-green-500 text-white"
                : "bg-primary-600 text-white"
            )}>
              {hasActiveForms ? <Check className="w-5 h-5" /> : '1'}
            </div>
            <div className="flex-1 pt-1">
              <h4 className="text-base font-semibold text-gray-900">
                {hasActiveForms ? 'Your application form is ready!' : 'Create your application form'}
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                {hasActiveForms
                  ? 'Applicants will fill this out when they apply to your jobs.'
                  : 'Choose what info you want from applicants — name, resume, experience, anything you need.'}
              </p>
              {!hasAnyForms && (
                <button
                  onClick={() => navigate('/forms/new')}
                  className="mt-3 btn-primary inline-flex items-center gap-2"
                >
                  Create Your Form <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {hasAnyForms && !hasActiveForms && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <p className="text-sm text-yellow-800">
                    You have {allForms.length} form{allForms.length > 1 ? 's' : ''} but none are active.{' '}
                    <button onClick={() => navigate('/forms')} className="font-semibold underline">
                      Activate a form
                    </button>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* STEP 2 */}
          <div className="flex gap-4">
            <div className={cn(
              "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm",
              hasActiveForms ? "bg-primary-600 text-white" : "bg-gray-200 text-gray-400"
            )}>
              2
            </div>
            <div className="flex-1 pt-1">
              <h4 className={cn("text-base font-semibold", hasActiveForms ? "text-gray-900" : "text-gray-400")}>
                Copy your application link
              </h4>

              {hasActiveForms ? (
                <div className="mt-3 space-y-3">
                  {activeForms.map((form: any) => (
                    <div key={form.id} className="rounded-xl border border-gray-200 overflow-hidden">
                      <div className="flex items-center gap-3 p-4">
                        <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-5 h-5 text-primary-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">{form.name}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{form.job_title}</p>
                        </div>
                      </div>
                      <div className="px-4 pb-4">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            readOnly
                            value={form.public_url}
                            className="flex-1 input bg-gray-50 text-sm"
                          />
                          <button
                            onClick={() => copyToClipboard(form.public_url, `form-${form.id}`)}
                            className={cn(
                              "px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2",
                              copied === `form-${form.id}`
                                ? "bg-green-500 text-white"
                                : "bg-primary-600 text-white hover:bg-primary-700"
                            )}
                          >
                            {copied === `form-${form.id}` ? (
                              <><Check className="w-4 h-4" /> Copied!</>
                            ) : (
                              <><Copy className="w-4 h-4" /> Copy Link</>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mt-1">Create a form first, then you'll get a link to share.</p>
              )}
            </div>
          </div>

          {/* STEP 3 */}
          <div className="flex gap-4">
            <div className={cn(
              "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm",
              hasActiveForms ? "bg-primary-600 text-white" : "bg-gray-200 text-gray-400"
            )}>
              3
            </div>
            <div className="flex-1 pt-1">
              <h4 className={cn("text-base font-semibold", hasActiveForms ? "text-gray-900" : "text-gray-400")}>
                Add the link to your job postings
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                When candidates click "Apply" on Indeed or LinkedIn, they'll go straight to your form.
              </p>

              {hasActiveForms && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Indeed */}
                  <div className="rounded-xl border-2 border-gray-100 p-5 hover:border-[#2164f3]/30 hover:bg-blue-50/30 transition-all">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#2164f3] rounded-xl flex items-center justify-center shadow-sm">
                        <span className="text-white font-bold text-sm">in</span>
                      </div>
                      <span className="font-semibold text-gray-900">Indeed</span>
                    </div>
                    <div className="space-y-2.5 text-sm text-gray-600">
                      <div className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-bold">1</span>
                        <span>Open your job on Indeed</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-bold">2</span>
                        <span>Edit &rarr; How to apply</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-bold">3</span>
                        <span>Choose <strong>"My website"</strong></span>
                      </div>
                      <div className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#2164f3] text-white flex items-center justify-center text-xs font-bold">4</span>
                        <span><strong>Paste your link</strong> &rarr; Save</span>
                      </div>
                    </div>
                  </div>

                  {/* LinkedIn */}
                  <div className="rounded-xl border-2 border-gray-100 p-5 hover:border-[#0a66c2]/30 hover:bg-blue-50/30 transition-all">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#0a66c2] rounded-xl flex items-center justify-center shadow-sm">
                        <span className="text-white font-bold text-sm">in</span>
                      </div>
                      <span className="font-semibold text-gray-900">LinkedIn</span>
                    </div>
                    <div className="space-y-2.5 text-sm text-gray-600">
                      <div className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-bold">1</span>
                        <span>Post or edit a job</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-bold">2</span>
                        <span>How to receive applications</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-bold">3</span>
                        <span>Choose <strong>"External link"</strong></span>
                      </div>
                      <div className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#0a66c2] text-white flex items-center justify-center text-xs font-bold">4</span>
                        <span><strong>Paste your link</strong> &rarr; Publish</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {hasActiveForms && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-green-800">
                      <strong>That's it!</strong> When someone applies, they'll show up in your{' '}
                      <button onClick={() => navigate('/applicants')} className="font-semibold underline">
                        Applicants
                      </button>{' '}
                      page automatically. You can email them, track their status, and schedule interviews.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ============================= */}
      {/* COMPANY INFO                   */}
      {/* ============================= */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-primary-600" />
          <h3 className="font-medium text-gray-900">Your Company</h3>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            className="input"
            placeholder="Your company name"
          />
        </div>
      </div>

      {/* ============================= */}
      {/* INTERVIEW SCHEDULING           */}
      {/* ============================= */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-primary-600" />
          <h3 className="font-medium text-gray-900">Interview Scheduling</h3>
        </div>
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
          <p className="text-sm text-blue-800">
            <strong>Connect Calendly</strong> to let applicants self-schedule interviews. When you invite a candidate, they'll get a link to book a time that works for both of you.
          </p>
          <p className="text-xs text-blue-600 mt-2">
            Don't have Calendly?{' '}
            <a href="https://calendly.com/signup" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
              Create a free account →
            </a>
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Calendly Link</label>
          <div className="relative">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="url"
              value={formData.calendlyLink}
              onChange={(e) => setFormData({ ...formData, calendlyLink: e.target.value })}
              className="input pl-10"
              placeholder="https://calendly.com/your-name"
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">
            This link is sent to applicants when you invite them to interview
          </p>
        </div>
      </div>

      {/* ============================= */}
      {/* SMS INTEGRATION                */}
      {/* ============================= */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-5 h-5 text-primary-600" />
          <h3 className="font-medium text-gray-900">SMS Integration (httpSMS)</h3>
        </div>
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
          <p className="text-sm text-blue-800">
            Connect your <strong>httpSMS</strong> account to send and receive SMS from applicants.
            httpSMS uses your Android phone as an SMS gateway — no carrier registration required.
          </p>
          <p className="text-xs text-blue-600 mt-2">
            <a href="https://httpsms.com" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
              Set up httpSMS →
            </a>
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
          <div className="relative">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={formData.httpsmsApiKey}
              onChange={(e) => setFormData({ ...formData, httpsmsApiKey: e.target.value })}
              className="input pr-10"
              placeholder={data?.workspace?.httpsmsApiKey ? 'Key saved — enter new key to change' : 'Your httpSMS API key'}
            />
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
          <input
            type="tel"
            value={formData.httpsmsPhone}
            onChange={(e) => setFormData({ ...formData, httpsmsPhone: e.target.value })}
            className="input"
            placeholder="+1XXXXXXXXXX (the number on your SIM)"
          />
          <p className="text-xs text-gray-400 mt-1">
            Your Android phone must be running the httpSMS app and connected to the internet.
          </p>
        </div>
      </div>

      {/* ============================= */}
      {/* BRANDING & APPEARANCE          */}
      {/* ============================= */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Palette className="w-5 h-5 text-primary-600" />
          <h3 className="font-medium text-gray-900">Branding & Appearance</h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Display Name</label>
          <input
            type="text"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            className="input"
            placeholder="e.g. Acme Recruiting"
          />
          <p className="text-xs text-gray-400 mt-1">
            Shown on your application forms. If empty, your organization name will be used.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
          <div className="flex items-center gap-4">
            {logoPreview && (
              <img
                src={logoPreview}
                alt="Company logo"
                className="w-12 h-12 rounded-lg object-contain border border-gray-200"
              />
            )}
            <button
              type="button"
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadLogo.isPending}
              className="btn-secondary inline-flex items-center gap-2 text-sm"
            >
              <Upload className="w-4 h-4" />
              {uploadLogo.isPending ? 'Uploading...' : logoPreview ? 'Change Logo' : 'Upload Logo'}
            </button>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">
            This logo replaces the default icon on your application forms
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Form Background Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.formBackgroundColor}
                onChange={(e) => setFormData({ ...formData, formBackgroundColor: e.target.value })}
                className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
              />
              <input
                type="text"
                value={formData.formBackgroundColor}
                onChange={(e) => setFormData({ ...formData, formBackgroundColor: e.target.value })}
                className="input flex-1 font-mono text-sm"
                placeholder="#f0f4ff"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.primaryColor}
                onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
              />
              <input
                type="text"
                value={formData.primaryColor}
                onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                className="input flex-1 font-mono text-sm"
                placeholder="#2563eb"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ============================= */}
      {/* ADVANCED SETTINGS              */}
      {/* ============================= */}
      <div className="card overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="w-full flex items-center gap-3 p-5 text-left hover:bg-gray-50 transition-colors"
        >
          <Settings2 className="w-5 h-5 text-primary-600" />
          <h3 className="font-medium text-gray-900 flex-1">Advanced Settings</h3>
          {advancedOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {advancedOpen && (
          <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
            {/* Indeed Webhook URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Indeed Webhook URL</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${API_URL}/webhooks/indeed/${workspaceSlug}`}
                  className="input flex-1 bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={() => copyToClipboard(`${API_URL}/webhooks/indeed/${workspaceSlug}`, 'indeed-webhook')}
                  className={cn(
                    "px-3 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-1.5 flex-shrink-0",
                    copied === 'indeed-webhook'
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  {copied === 'indeed-webhook' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'indeed-webhook' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                For automatic import — paste this in your Indeed ATS integration to auto-receive applications
              </p>
            </div>


          </div>
        )}
      </div>

      {/* Save */}
      <div className="flex items-center justify-end gap-3 pb-8">
        {saved && (
          <span className="text-sm text-green-600 inline-flex items-center gap-1.5 font-medium">
            <CheckCircle className="w-4 h-4" /> Saved!
          </span>
        )}
        <button
          onClick={handleSave}
          disabled={updateWorkspace.isPending}
          className="btn-primary px-8"
        >
          {updateWorkspace.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
